import { useState, useCallback, useEffect } from 'react';
import { Clipboard as ClipboardIcon, Trash2, Search, Plus, Copy, Pencil, X, Check, FileCode, Link, FileText, CloudOff } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { api } from '../services/api.js';
import { useApi } from '../hooks/useApi.js';
import { useStorage, useSyncQueue } from '../hooks/useStorage.js';
import { useFocusRefresh } from '../hooks/useFocusRefresh.js';
import { useNotificationRefresh } from '../hooks/useNotificationRefresh.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { formatDate, cn } from '../lib/utils.js';
import { localDB } from '../services/db.js';

const CLIP_TYPES = [
  { value: 'text', label: 'Text', icon: FileText },
  { value: 'code', label: 'Code', icon: FileCode },
  { value: 'url', label: 'URL', icon: Link },
];

const detectClipType = (content) => {
  if (!content) return 'text';
  const trimmed = content.trim();
  
  if (/^https?:\/\//.test(trimmed) || /^www\./.test(trimmed)) return 'url';
  
  const codePatterns = [
    /[{};]/, /==/, /\bfunction\b/, /\bconst\b/, /\blet\b/, /\bvar\b/,
    /\bif\s*\(/, /\bfor\s*\(/, /\bwhile\s*\(/, /\breturn\b/,
    /\bsudo\b/, /\bssh\b/, /\bdocker\b/, /\bkubectl\b/,
    /\bshow\s+(run|ip)\b/, /^#!\//m, /\bimport\b.*from/, /\bexport\b/, /=>/, /\|\|/, /&&/,
  ];
  
  const lines = trimmed.split('\n');
  const hasMultipleLines = lines.length > 1;
  const hasIndentation = lines.some(line => /^\s{2,}/.test(line));
  const hasCodePattern = codePatterns.some(pattern => pattern.test(trimmed));
  
  if (hasCodePattern || (hasMultipleLines && hasIndentation)) return 'code';
  return 'text';
};

const getTypeBadge = (type) => {
  const badge = CLIP_TYPES.find(t => t.value === type) || CLIP_TYPES[0];
  const Icon = badge.icon;
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
      type === 'code' && 'bg-emerald-500/20 text-emerald-400',
      type === 'url' && 'bg-blue-500/20 text-blue-400',
      type === 'text' && 'bg-zinc-500/20 text-zinc-400',
    )}>
      <Icon size={10} />
      {badge.label}
    </span>
  );
};

const isTempId = (id) => String(id).startsWith('temp-');

// Normalize any timestamp to milliseconds for reliable sorting
const toMs = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val < 1e12 ? val * 1000 : val; // seconds → ms
  const t = new Date(val).getTime();
  return isNaN(t) ? 0 : t;
};

export default function Clipboard() {
  const [q, setQ] = useState('');
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editType, setEditType] = useState('text');
  const [copiedId, setCopiedId] = useState(null);
  const toast = useToast();

  const { data: localClips, loading: localLoading, refresh: refreshClips, saveItem, removeItem } = useStorage('clips');
  const { queue, addToQueue, removeFromQueue } = useSyncQueue();

  const { data: apiData, loading: apiLoading, reload } = useApi(
    () => api.listClips(q ? `q=${encodeURIComponent(q)}&limit=100` : 'limit=100'),
    [q]
  );

  // Sync API data to IndexedDB
  useEffect(() => {
    if (apiData?.data?.items) {
      const items = apiData.data.items;
      const syncToIDB = async () => {
        let changed = false;

        // 1. Prune deleted items if not searching
        if (!q) {
          const apiIds = new Set(items.map(c => c.id));
          const timestamps = items.map(c => toMs(c.created_at)).filter(t => t > 0);
          const oldestApiTime = timestamps.length > 0 ? Math.min(...timestamps) : 0;

          const currentLocal = await localDB.getAll('clips');
          for (const clip of currentLocal) {
            // If it's a server-synced clip (not pending, not temp) and not in the apiIds set,
            // and it is newer than or equal to the oldest clip returned (to avoid pruning older clips that fell off the list limit)
            if (!clip.pending && !isTempId(clip.id) && !apiIds.has(clip.id)) {
              if (toMs(clip.created_at) >= oldestApiTime) {
                await localDB.delete('clips', clip.id);
                changed = true;
              }
            }
          }
        }

        // 2. Save new or updated items
        for (const item of items) {
          const existing = await localDB.get('clips', item.id);
          // Overwrite if it doesn't exist, or if the server version is different and local has no pending edits
          if (!existing || (!existing.pending && (existing.content !== item.content || existing.type !== item.type))) {
            await localDB.put('clips', item);
            changed = true;
          }
        }

        // 3. Refresh React state if any database change happened
        if (changed) {
          await refreshClips();
        }
      };
      syncToIDB();
    }
  }, [apiData, q, refreshClips]);

  // Merge and filter items — UI reads ONLY from IndexedDB
  const items = (localClips || [])
    .filter(c => !q || c.content.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => toMs(b.created_at) - toMs(a.created_at));

  useFocusRefresh(reload);
  useNotificationRefresh(reload, 'syncvault:refresh:clips');

  const copy = async (clipId, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); } catch { toast.error('Could not copy'); }
      document.body.removeChild(textarea);
    }
    setCopiedId(clipId);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 1500);
  };

  // CREATE: write to IndexedDB first with tempId, call API if online, replace on success
  const save = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    const detectedType = detectClipType(content);
    const tempId = `temp-${Date.now()}`;
    const newClip = { 
      id: tempId, 
      content, 
      source: 'web', 
      type: detectedType, 
      created_at: new Date().toISOString(),
      pending: true 
    };

    // 1. Save to IndexedDB immediately
    await saveItem(newClip);
    setContent('');

    // 2. Try API if online
    if (navigator.onLine) {
      try {
        const res = await api.createClip({ content: newClip.content, source: newClip.source, type: newClip.type });
        // 3. Replace temp with server data
        await removeItem(tempId);
        await saveItem(res.data);
        toast.success('Clip saved');
        return;
      } catch (err) {
        console.warn('[Clipboard] API create failed, staying offline:', err);
      }
    }

    // 4. Offline or API failed — keep temp in IndexedDB, queue for later sync
    await addToQueue({ type: 'CREATE_CLIP', data: { content: newClip.content, source: newClip.source, type: newClip.type }, tempId });
    toast.info('Saved locally (offline)');
  };

  // DELETE: if tempId only, remove from IndexedDB + queue. If serverId, queue delete.
  const remove = async (id) => {
    if (!confirm('Delete this clip?')) return;
    
    // 1. Remove from IndexedDB immediately
    await removeItem(id);

    // 2. If temp-only clip, also purge any pending queue entries for it
    if (isTempId(id)) {
      await localDB.removeQueueEntriesByTempId(id);
      toast.info('Clip removed');
      return;
    }

    // 3. Server clip — try API if online
    if (navigator.onLine) {
      try {
        await api.deleteClip(id);
        toast.info('Clip removed');
        return;
      } catch (err) {
        console.warn('[Clipboard] API delete failed, queuing:', err);
      }
    }

    // 4. Offline or API failed — queue delete with serverId
    await addToQueue({ type: 'DELETE_CLIP', serverId: id });
    toast.info('Removed locally (offline)');
  };

  const startEdit = useCallback((clip) => {
    setEditingId(clip.id);
    setEditContent(clip.content);
    setEditType(clip.type || detectClipType(clip.content));
  }, []);

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditType('text');
  };

  const saveEdit = async (id) => {
    if (!editContent.trim()) return;
    const newType = detectClipType(editContent);
    const originalClip = items.find(i => i.id === id);
    const updatedClip = { ...originalClip, content: editContent, type: newType, updated_at: Date.now(), pending: true };

    // 1. Update IndexedDB immediately
    await saveItem(updatedClip);
    setEditingId(null);

    // 2. If temp clip, don't try API — it will sync on create
    if (isTempId(id)) {
      toast.success('Clip updated');
      return;
    }

    // 3. Try API if online
    if (navigator.onLine) {
      try {
        const result = await api.updateClip(id, { content: editContent, type: newType });
        await saveItem(result.data);
        toast.success('Clip updated');
        return;
      } catch (err) {
        console.warn('[Clipboard] API update failed, queuing:', err);
      }
    }

    // 4. Queue for sync
    await addToQueue({ type: 'UPDATE_CLIP', serverId: id, data: { content: editContent, type: newType } });
    toast.info('Updated locally (offline)');
  };

  const handleEditContentChange = (e) => {
    const newContent = e.target.value;
    setEditContent(newContent);
    const detected = detectClipType(newContent);
    setEditType(detected);
  };

  return (
    <div>
      <PageHeader
        title="Clipboard"
        subtitle="Your cross-device text history"
        actions={
          <div className="flex items-center gap-2">
            {queue.length > 0 && (
              <div className="flex items-center gap-1 text-amber-500 text-xs font-medium bg-amber-500/10 px-2 py-1 rounded-md" title="Pending synchronization">
                <CloudOff size={12} />
                <span>{queue.length} pending</span>
              </div>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input className="input pl-7" placeholder="Search clips" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
        }
      />

      <form onSubmit={save} className="card mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="label">New clip</label>
          <textarea 
            className="input min-h-[60px]" 
            value={content} 
            onChange={(e) => setContent(e.target.value)} 
            placeholder="Paste or type something to save..." 
          />
        </div>
        <button className="btn-primary self-stretch sm:self-end" type="submit">
          <Plus size={14} /> Save
        </button>
      </form>

      {localLoading && apiLoading && items.length === 0 && <div className="card text-sm text-zinc-400">Loading clips...</div>}
      {!localLoading && !apiLoading && items.length === 0 && (
        <EmptyState icon={ClipboardIcon} title="No clips yet" description="Save your first snippet above to get started." />
      )}

      <ul className="space-y-2">
        {items.map((c) => (
          <li key={c.id} className={cn(
            'card-hover flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between transition-all duration-150',
            c.pending && 'opacity-70 border-amber-500/30',
            copiedId === c.id && 'ring-2 ring-accent/60 border-accent/30',
          )}>
            <div className="min-w-0 flex-1">
              {editingId === c.id ? (
                <div className="space-y-2">
                  <textarea
                    className={cn(
                      'input min-h-[80px] w-full text-sm',
                      editType === 'code' && 'font-mono'
                    )}
                    value={editContent}
                    onChange={handleEditContentChange}
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-zinc-500">Type:</label>
                    <span className="text-xs font-medium">{editType}</span>
                    {getTypeBadge(editType)}
                  </div>
                </div>
              ) : (
                <pre className={cn(
                  'whitespace-pre-wrap break-words text-sm text-zinc-100',
                  c.type === 'code' && 'font-mono bg-zinc-900/50 p-2 rounded'
                )}>
                  {c.content}
                </pre>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span>{formatDate(c.created_at)}</span>
                {getTypeBadge(c.type || 'text')}
                {c.pending && (
                  <span className="text-amber-500 flex items-center gap-1">
                    <CloudOff size={10} /> Offline
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 sm:flex-col">
              {editingId === c.id ? (
                <>
                  <button
                    className="btn-primary !px-2 !py-1.5"
                    onClick={() => saveEdit(c.id)}
                    title="Save"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    className="btn-ghost !px-2 !py-1.5"
                    onClick={cancelEdit}
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn-ghost"
                    onClick={() => startEdit(c)}
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => copy(c.id, c.content)}
                    title="Copy to clipboard"
                  >
                    <Copy size={14} />
                  </button>
                  <button className="btn-danger" onClick={() => remove(c.id)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
